import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import { useTimeRangeSelection } from 'hooks/useTimeRangeSelection';
import {
  allFilters,
  DateRangeFilter,
  RangePickerQueryParamKeys,
} from '@cast/shared-date-ui';

export const PV_EVENTS_QUERY_PARAM_KEYS: RangePickerQueryParamKeys = {
  fromDate: 'pv_events_from',
  toDate: 'pv_events_to',
  dateRangePreset: 'pv_events_date_preset',
  timeZone: 'pv_events_timezone',
};

const PV_EVENTS_FILTER_PRESETS = [
  DateRangeFilter.Last15Mins,
  DateRangeFilter.Last30Mins,
  DateRangeFilter.LastHour,
  DateRangeFilter.Last6Hours,
  DateRangeFilter.Last24Hours,
  DateRangeFilter.Last7Days,
  DateRangeFilter.Last2Weeks,
  DateRangeFilter.Last30Days,
  DateRangeFilter.Last3Months,
  DateRangeFilter.Last12Months,
] as const;

const DEFAULT_PV_EVENTS_PRESET = DateRangeFilter.Last7Days;

export const MAX_PV_EVENTS_DAYS = 365;

type FilterPresetsByMinDateArgs = {
  minDate?: string;
  timezone?: string;
};

const filterPvEventsPresetsByMinDate = ({
  minDate,
  timezone,
}: FilterPresetsByMinDateArgs): DateRangeFilter[] => {
  if (!minDate) {
    return [...PV_EVENTS_FILTER_PRESETS];
  }

  const minDay = dayjs.utc(minDate);

  if (!minDay.isValid()) {
    return [...PV_EVENTS_FILTER_PRESETS];
  }

  return PV_EVENTS_FILTER_PRESETS.filter((preset) => {
    const { from } = allFilters[preset].date(timezone, true);
    return !dayjs.utc(from).isBefore(minDay);
  });
};

type UsePvEventsTimeRangeSelectionArgs = {
  minDate?: string;
};

export const usePvEventsTimeRangeSelection = ({
  minDate,
}: UsePvEventsTimeRangeSelectionArgs = {}) => {
  const [searchParams] = useSearchParams();

  const timezone =
    searchParams.get(PV_EVENTS_QUERY_PARAM_KEYS.timeZone) || dayjs.tz.guess();

  const filterPresets = filterPvEventsPresetsByMinDate({
    minDate,
    timezone,
  });

  const timeRange = useTimeRangeSelection({
    queryParamKeys: PV_EVENTS_QUERY_PARAM_KEYS,
    initialState: {
      preset:
        filterPresets.length === 0 ||
        filterPresets.includes(DEFAULT_PV_EVENTS_PRESET)
          ? DEFAULT_PV_EVENTS_PRESET
          : filterPresets[filterPresets.length - 1],
    },
    constraints: {
      maxNumberOfDays: MAX_PV_EVENTS_DAYS,
      ...(minDate ? { minDate } : {}),
    },
    allowTimeInput: true,
  });

  return {
    ...timeRange,
    filterPresets,
  };
};
